import _objectDestructuringEmpty from "@babel/runtime/helpers/esm/objectDestructuringEmpty";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "hasCloseButton", "isOpen", "onClose", "inlineSize"],
  _excluded2 = ["as", "children", "color", "paddingBlock", "paddingInlineEnd"],
  _excluded3 = ["children", "flexGrow", "padding"],
  _excluded4 = ["children", "hasAlert", "paddingBlock", "paddingInline"],
  _excluded5 = ["children", "flush"];
import { useState, forwardRef } from 'react';
import { Heading } from '../Typography/Heading';
import { LayoutBox } from '../Layout/LayoutBox';
import { Panel, PanelSection } from 'foundations-components/transitional/components/Panel';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { PanelHeader } from 'foundations-components/transitional/components/PanelHeader';
import { PanelFooter } from 'foundations-components/transitional/components/PanelFooter';
import { PanelBody } from 'foundations-components/transitional/components/PanelBody';
import { useSpacingProps } from '../Layout/useSpacingProps';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const useDrawer = (defaultIsOpen = false) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(defaultIsOpen);
  const handleDrawerToggle = () => setIsDrawerOpen(!isDrawerOpen);
  return {
    isDrawerOpen,
    handleDrawerToggle
  };
};
export const Drawer = _ref => {
  let {
      children,
      hasCloseButton = true,
      isOpen,
      onClose,
      inlineSize = 860
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  if (!isOpen) {
    return null;
  }
  return /*#__PURE__*/_jsxs(Panel, Object.assign({}, rest, {
    width: inlineSize,
    children: [hasCloseButton ? /*#__PURE__*/_jsx(DialogCloseButton, {
      onClick: onClose
    }) : null, children]
  }));
};
const Header = _ref2 => {
  let restWithSpacingProps = Object.assign({}, (_objectDestructuringEmpty(_ref2), _ref2));
  const {
    spacingProps,
    otherProps: rest
  } = useSpacingProps(restWithSpacingProps);
  return /*#__PURE__*/_jsx(PanelHeader, Object.assign({}, rest, spacingProps));
};
Drawer.Header = Header;
Drawer.Heading = _ref3 => {
  let {
      as = 'h4',
      children,
      color = 'inverse',
      paddingBlock = 'space-sm',
      paddingInlineEnd = 'space-sm'
    } = _ref3,
    rest = _objectWithoutPropertiesLoose(_ref3, _excluded2);
  return /*#__PURE__*/_jsx(Heading, Object.assign({}, rest, {
    as: as,
    color: color,
    paddingBlock: paddingBlock,
    paddingInlineEnd: paddingInlineEnd,
    children: children
  }));
};
const Footer = _ref4 => {
  let restWithSpacingProps = Object.assign({}, (_objectDestructuringEmpty(_ref4), _ref4));
  const {
    spacingProps,
    otherProps: rest
  } = useSpacingProps(restWithSpacingProps);
  return /*#__PURE__*/_jsx(PanelFooter, Object.assign({}, rest, spacingProps));
};
Drawer.Footer = Footer;
const Body = /*#__PURE__*/forwardRef((_ref5, ref) => {
  let restWithSpacingProps = Object.assign({}, (_objectDestructuringEmpty(_ref5), _ref5));
  const {
    spacingProps,
    otherProps: rest
  } = useSpacingProps(restWithSpacingProps);
  return /*#__PURE__*/_jsx(PanelBody, Object.assign({
    ref: ref
  }, rest, spacingProps));
});
Body.displayName = 'Drawer.Body';
Drawer.Body = Body;
const Content = _ref6 => {
  let {
      children,
      flexGrow = 1,
      padding = 'space-xl'
    } = _ref6,
    rest = _objectWithoutPropertiesLoose(_ref6, _excluded3);
  return /*#__PURE__*/_jsx(LayoutBox, Object.assign({}, rest, {
    flexGrow: flexGrow,
    padding: padding,
    children: children
  }));
};
Drawer.Content = Content;
const Alert = _ref7 => {
  let {
      children,
      hasAlert = false,
      paddingBlock = 'space-md',
      paddingInline = 'space-xl'
    } = _ref7,
    rest = _objectWithoutPropertiesLoose(_ref7, _excluded4);
  if (!hasAlert) {
    return null;
  }
  return /*#__PURE__*/_jsx(LayoutBox, Object.assign({
    paddingBlock: paddingBlock,
    paddingInline: paddingInline
  }, rest, {
    children: children
  }));
};
Drawer.Alert = Alert;
const Section = _ref8 => {
  let {
      children,
      flush = true
    } = _ref8,
    restWithSpacingProps = _objectWithoutPropertiesLoose(_ref8, _excluded5);
  const {
    spacingProps,
    otherProps: rest
  } = useSpacingProps(restWithSpacingProps);
  return /*#__PURE__*/_jsx(PanelSection, Object.assign({}, rest, spacingProps, {
    flush: flush,
    children: children
  }));
};
Drawer.Section = Section;